import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Toolbar,
  Fade,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material'
import { Download } from '@mui/icons-material'
import { leadStatuses } from '../../../../constants'
import axiosInstance from '../../../../utils/axiosInstance'

interface BulkLeadActionsProps {
  selectedLeads: string[]
  onClearSelection: () => void
  onStatusUpdate: () => void
  orderId?: string
}

const BulkLeadActions: React.FC<BulkLeadActionsProps> = ({
  selectedLeads,
  onClearSelection,
  onStatusUpdate,
  orderId,
}) => {
  const [selectedStatus, setSelectedStatus] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [rejectionConfirmOpen, setRejectionConfirmOpen] = useState(false)

  const handleStatusUpdate = () => {
    if (!selectedStatus || selectedLeads.length === 0) return
    if (selectedStatus === 'rejected') {
      setRejectionConfirmOpen(true)
      return
    }
    applyBulkStatusUpdate(false)
  }

  const applyBulkStatusUpdate = async (sendRejectionEmail: boolean) => {
    setIsUpdating(true)
    try {
      await axiosInstance.patch('/lead/bulk-update-status', {
        leadIds: selectedLeads,
        status: selectedStatus,
        sendRejectionEmail,
        orderId,
      })
      setShowSuccess(true)
      onStatusUpdate()
      onClearSelection()
      setSelectedStatus('')
    } catch (error) {
      console.error('Error updating lead statuses:', error)
      setErrorMessage('Failed to update lead statuses. Please try again.')
      setShowError(true)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleRejectionConfirm = (sendEmail: boolean) => {
    setRejectionConfirmOpen(false)
    applyBulkStatusUpdate(sendEmail)
  }

  const handleCancel = () => {
    onClearSelection()
    setSelectedStatus('')
  }

  const handleExportSelected = async () => {
    if (!orderId || selectedLeads.length === 0) return

    setIsExporting(true)
    try {
      const leadIdsParam = selectedLeads.join(',')
      const response = await axiosInstance.get(`/leads-by-order/${orderId}/download`, {
        params: { leadIds: leadIdsParam },
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fileName = `order-${orderId}-selected-leads.csv`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      // Update localStorage to mark selected leads as downloaded
      const storageKey = `downloaded-leads-order-${orderId}`
      const downloadedLeadsJson = localStorage.getItem(storageKey)
      const previouslyDownloaded = downloadedLeadsJson ? JSON.parse(downloadedLeadsJson) : []
      const updatedDownloadedLeads = [...new Set([...previouslyDownloaded, ...selectedLeads])]
      localStorage.setItem(storageKey, JSON.stringify(updatedDownloadedLeads))

      setShowSuccess(true)
    } catch (error) {
      console.error('Error exporting selected leads:', error)
      setErrorMessage('Failed to export selected leads. Please try again.')
      setShowError(true)
    } finally {
      setIsExporting(false)
    }
  }

  if (selectedLeads.length === 0) return null

  return (
    <>
      <Fade in={selectedLeads.length > 0}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 1,
            mb: 2,
            boxShadow: 2,
          }}
        >
          <Toolbar>
            <Typography
              variant="h6"
              component="div"
              sx={{
                flexGrow: 1,
                color: 'white',
                fontWeight: 500,
              }}
            >
              {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    },
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                  id="status-select-label"
                >
                  New Status
                </InputLabel>
                <Select
                  labelId="status-select-label"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="New Status"
                  sx={{
                    color: 'primary.contrastText',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.contrastText',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.contrastText',
                    },
                    '.MuiSvgIcon-root': {
                      color: 'primary.contrastText',
                    },
                  }}
                >
                  {leadStatuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleStatusUpdate}
                disabled={!selectedStatus || isUpdating}
                startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  whiteSpace: 'nowrap',
                  backgroundColor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                {isUpdating ? 'Updating...' : 'Apply Status'}
              </Button>

              {orderId && (
                <Button
                  variant="contained"
                  onClick={handleExportSelected}
                  disabled={isExporting}
                  startIcon={
                    isExporting ? <CircularProgress size={16} color="inherit" /> : <Download />
                  }
                  sx={{
                    whiteSpace: 'nowrap',
                    backgroundColor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    },
                  }}
                >
                  {isExporting ? 'Exporting...' : 'Export Selected'}
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isUpdating || isExporting}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                  '&:disabled': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Toolbar>
        </Box>
      </Fade>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Successfully updated {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''}!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Rejection confirmation dialog */}
      <Dialog
        open={rejectionConfirmOpen}
        onClose={() => setRejectionConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Rejection</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reject {selectedLeads.length} applicant
            {selectedLeads.length > 1 ? 's' : ''}? A rejection <strong>mail</strong> will be sent to
            each applicant.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => handleRejectionConfirm(false)} color="error" variant="outlined">
            Reject without email
          </Button>
          <Button onClick={() => handleRejectionConfirm(true)} color="error" variant="contained">
            Reject &amp; send mail
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BulkLeadActions
